/**
 * SyncHealthBanner — a single line, only when something is genuinely at risk.
 *
 * ── What this is not ───────────────────────────────────────────────────────
 * The first version was a multi-line block dropped into the header's inline
 * notice strip, which is a one-line flex row. It wrapped to one word per line
 * and covered the store name. It also showed a standing warning about browser
 * storage to every web user, which read as "do not use the web version" — on a
 * till whose data was fully synced and in no danger at all.
 *
 * Both faults came from the same mistake: treating a background risk as though
 * it were an emergency. The rule now is that this appears ONLY when there is
 * unsynced work or a bill the server has not got, and when it does appear it
 * occupies one line and says one thing. Everything else lives in the footer
 * chip, or nowhere.
 */
import { useState } from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { SyncHealth } from '@/sync/useSyncHealth';

export function SyncHealthBanner({
  health,
  onSyncNow,
  isSyncing,
}: {
  health: SyncHealth;
  onSyncNow: () => void;
  isSyncing: boolean;
}) {
  const [dismissed, setDismissed] = useState(false);

  // `info` stays in the footer chip. Only warn and alert are worth the header.
  if ((health.severity !== 'warn' && health.severity !== 'alert') || !health.message) return null;

  const missing = health.reconcile?.missingOnServer ?? [];
  const unrecoverable = missing.length > 0;
  const renumbered = health.renumbered;

  // A warning may be set aside; a sale the server has never seen may not,
  // because unlike a dropped connection it does not fix itself.
  if (dismissed && !unrecoverable) return null;

  // The full story goes in the tooltip so the strip stays one line.
  const detail = unrecoverable
    ? `${health.message}\n\n${missing.slice(0, 20).join(', ')}${missing.length > 20 ? `, and ${missing.length - 20} more` : ''}`
    : health.message;

  const short = renumbered.length > 0
    ? renumbered.length === 1
      ? `${renumbered[0].from} is now ${renumbered[0].to}`
      : `${renumbered.length} bills renumbered`
    : unrecoverable
      ? `${missing.length} ${missing.length === 1 ? 'bill' : 'bills'} not on the server`
      : `${health.pending} not synced${health.oldestAt != null ? ` · ${health.ageLabel}` : ''}`;

  return (
    <div
      role="status"
      title={detail}
      className={cn(
        'flex min-w-0 max-w-sm items-center gap-1.5 rounded-md px-2 py-1 text-label',
        health.severity === 'alert'
          ? 'bg-danger/15 text-danger-ink'
          : 'bg-warning/15 text-warning-ink',
      )}
    >
      <AlertTriangle size={12} className="shrink-0" />
      <span className="truncate normal-case">{short}</span>
      <button
        type="button"
        onClick={onSyncNow}
        disabled={isSyncing}
        className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 transition-colors hover:bg-black/10 disabled:opacity-50"
      >
        <RefreshCw size={11} className={cn(isSyncing && 'animate-spin')} />
        <span className="sr-only">Sync now</span>
      </button>
      {!unrecoverable && (
        <button
          type="button"
          onClick={() => {
            // For a renumbering this is an acknowledgement — the operator has
            // seen which bill changed — so it clears the record rather than
            // hiding a message that would come straight back next cycle.
            if (renumbered.length > 0) health.acknowledgeRenumbering();
            else setDismissed(true);
          }}
          aria-label={renumbered.length > 0 ? 'Got it' : 'Hide'}
          className="shrink-0 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100"
        >
          <X size={11} />
        </button>
      )}
    </div>
  );
}
